import { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { StateBadge } from '../components/StateBadge';
import { GlassCard } from '../components/GlassCard';
import { Toast } from '../components/Toast';
import { TypingDots } from '../components/TypingDots';
import { api, ApiError } from '../lib/api';
import { useTaskStore } from '../stores/useTaskStore';
import { useNotificationStore } from '../stores/useNotificationStore';
import { T } from '../lib/design-tokens';
import type { ClassificationResult, ContactSource, NotificationRecord } from '../types';

interface ClassifyTaskCreated {
  taskId: string;
  queryLogId: string;
  messageType: 'classification';
  classification: ClassificationResult;
  state: 'AWAITING_APPROVAL';
  plan: {
    contactName: string | null;
    contactPhone: string | null;
    contactSource: ContactSource | null;
    inferredContext: string | null;
    talkingPoints: string | null;
    emailSubject: string | null;
    emailBody: string | null;
  };
  requiresContact: boolean;
  requiresPhoneAuth: boolean;
  requiresEmailAuth: boolean;
  message?: string;
}

interface ClassifyInfoRetrieval {
  taskId: null;
  queryLogId: string;
  messageType: 'info_retrieval';
  infoResponse: string;
}

interface ClassifyLowConfidence {
  taskId: null;
  queryLogId: string;
  messageType: 'classification';
  classification: ClassificationResult;
  message: string;
}

interface ClassifyUsageLimit {
  taskId: null;
  queryLogId: string;
  messageType: 'classification';
  usageLimitReached: true;
  callsLimit?: number;
  callsUsed?: number;
  emailsLimit?: number;
  emailsUsed?: number;
  message: string;
}

type ClassifyResponse =
  | ClassifyTaskCreated
  | ClassifyInfoRetrieval
  | ClassifyLowConfidence
  | ClassifyUsageLimit;

type InlineResult =
  | { type: 'info'; text: string }
  | { type: 'clarification'; intent: string; confidence: number; message: string }
  | { type: 'usage_limit'; message: string };

type Mode = 'standard' | 'aggressive' | 'relentless';

function inferStateFromTitle(title: string): string {
  if (title.includes('Completed')) return 'COMPLETED';
  if (title.includes('Failed')) return 'FAILED';
  if (title.includes('Executing')) return 'EXECUTING';
  if (title.includes('Waiting')) return 'WAITING_RESPONSE';
  if (title.includes('Cancelled')) return 'CANCELLED';
  return 'EXECUTING';
}

export function HomePage() {
  const [input, setInput] = useState('');
  const [focused, setFocused] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState<Mode>('standard');
  const [inlineResult, setInlineResult] = useState<InlineResult | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [showActivity, setShowActivity] = useState(false);
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const navigate = useNavigate();
  const setPendingTask = useTaskStore((s) => s.setPendingTask);
  const notifications = useNotificationStore((s) => s.notifications);

  useEffect(() => {
    setTimeout(() => setShowActivity(true), 500);
  }, []);

  useEffect(() => {
    api<NotificationRecord[]>('/notifications?unread=false')
      .then((data) => useNotificationStore.getState().setNotifications(data))
      .catch(() => {});
    api<{ count: number }>('/notifications/unread-count')
      .then((data) => useNotificationStore.getState().setUnreadCount(data.count))
      .catch(() => {});
  }, []);

  const handleSubmit = useCallback(async () => {
    const text = input.trim();
    if (!text || text.length > 2000 || loading) return;

    setLoading(true);
    setInlineResult(null);

    try {
      const res = await api<ClassifyResponse>('/task/classify', {
        method: 'POST',
        body: JSON.stringify({ user_input: text }),
      });

      if (res.messageType === 'info_retrieval') {
        setInlineResult({ type: 'info', text: (res as ClassifyInfoRetrieval).infoResponse });
        setInput('');
        return;
      }

      if ('usageLimitReached' in res && res.usageLimitReached) {
        setInlineResult({ type: 'usage_limit', message: (res as ClassifyUsageLimit).message });
        return;
      }

      if (res.taskId === null && res.messageType === 'classification') {
        const r = res as ClassifyLowConfidence;
        setInlineResult({
          type: 'clarification',
          intent: r.classification.intent,
          confidence: r.classification.confidence,
          message: r.message,
        });
        inputRef.current?.focus();
        return;
      }

      if (res.taskId && res.messageType === 'classification') {
        const r = res as ClassifyTaskCreated;
        setPendingTask({
          taskId: r.taskId,
          classification: r.classification,
          plan: r.plan,
          requiresContact: r.requiresContact,
          requiresPhoneAuth: r.requiresPhoneAuth,
          requiresEmailAuth: r.requiresEmailAuth,
          message: r.message,
        });
        setInput('');
        navigate(`/operations/${r.taskId}/approve`);
      }
    } catch (err) {
      setToast(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [input, loading, navigate, setPendingTask]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  const hasText = input.trim().length > 0;
  const activityItems = notifications.slice(0, 8);
  const activeOps = activityItems.filter((n) => n.type === 'task_update' && n.taskId).slice(0, 5);

  return (
    <div className="max-w-[800px] mx-auto relative" style={{ padding: '48px 40px' }}>
      {/* Ambient glow */}
      <div
        style={{
          position: 'absolute',
          top: -100,
          right: -60,
          width: 500,
          height: 500,
          pointerEvents: 'none',
          opacity: 0.4,
          zIndex: 0,
        }}
      >
        <svg viewBox="0 0 500 500" style={{ width: '100%', height: '100%' }}>
          <defs>
            <radialGradient id="gA" cx="40%" cy="40%">
              <stop offset="0%" stopColor={T.gold} stopOpacity="0.08" />
              <stop offset="100%" stopColor={T.gold} stopOpacity="0" />
            </radialGradient>
            <radialGradient id="gB" cx="60%" cy="60%">
              <stop offset="0%" stopColor={T.green} stopOpacity="0.05" />
              <stop offset="100%" stopColor={T.green} stopOpacity="0" />
            </radialGradient>
          </defs>
          <circle cx="200" cy="200" r="200" fill="url(#gA)" />
          <circle cx="300" cy="300" r="180" fill="url(#gB)" />
        </svg>
      </div>

      {/* Hero */}
      <div className="relative z-[1] mb-10" style={{ animation: 'fadeUp 0.8s ease both' }}>
        <h1
          style={{
            fontFamily: T.serif,
            fontSize: 42,
            fontWeight: 400,
            color: T.cream,
            lineHeight: 1.15,
            marginBottom: 12,
            letterSpacing: '-0.5px',
          }}
        >
          What do you need
          <br />
          <span style={{ fontStyle: 'italic', color: T.gold }}>handled</span>?
        </h1>
        <p
          style={{
            fontFamily: T.sans,
            fontSize: 15,
            color: T.cream35,
            lineHeight: 1.6,
            maxWidth: 420,
          }}
        >
          Describe the task. We figure out the plan, pick the channel, and get it done.
        </p>
      </div>

      {/* Input */}
      <div
        onClick={() => inputRef.current?.focus()}
        className="relative z-[1] cursor-text"
        style={{
          border: focused ? `1.5px solid ${T.gold}` : `1.5px solid ${T.glassBorder}`,
          borderRadius: 16,
          padding: '20px 24px 16px',
          marginBottom: 48,
          background: T.glass,
          backdropFilter: 'blur(20px)',
          boxShadow: focused
            ? `0 0 0 4px ${T.goldGlow}, 0 8px 40px rgba(0,0,0,0.3)`
            : '0 2px 12px rgba(0,0,0,0.2)',
          transition: 'all 0.3s cubic-bezier(0.4,0,0.2,1)',
          animation: 'fadeUp 0.6s ease 0.15s both',
        }}
      >
        <textarea
          ref={inputRef}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          onKeyDown={handleKeyDown}
          placeholder="Cancel my Hulu subscription... Dispute the $49 charge on my Amex... Get a better rate on my internet bill..."
          maxLength={2000}
          rows={3}
          style={{
            width: '100%',
            border: 'none',
            outline: 'none',
            resize: 'none',
            fontFamily: T.sans,
            fontSize: 15,
            lineHeight: 1.65,
            color: T.cream,
            background: 'transparent',
          }}
        />
        <div
          className="flex justify-between items-center"
          style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${T.cream04}` }}
        >
          <div className="flex gap-1.5">
            {(['standard', 'aggressive', 'relentless'] as const).map((m) => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                style={{
                  padding: '5px 12px',
                  borderRadius: 100,
                  fontSize: 11,
                  fontFamily: T.mono,
                  fontWeight: 500,
                  border: mode === m ? `1.5px solid ${T.gold}` : `1px solid ${T.glassBorder}`,
                  color: mode === m ? T.gold : T.cream35,
                  background: mode === m ? T.goldGlow : 'transparent',
                  cursor: 'pointer',
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  transition: 'all 0.2s',
                }}
              >
                {m}
              </button>
            ))}
          </div>
          <button
            type="button"
            onClick={() => handleSubmit()}
            disabled={!hasText || loading}
            style={{
              padding: '9px 24px',
              borderRadius: 10,
              border: 'none',
              background: hasText ? T.gold : T.cream08,
              color: hasText ? T.bg : T.cream20,
              fontSize: 13,
              fontWeight: 700,
              cursor: hasText ? 'pointer' : 'default',
              fontFamily: T.mono,
              letterSpacing: '0.05em',
              textTransform: 'uppercase',
              transition: 'all 0.25s cubic-bezier(0.4,0,0.2,1)',
            }}
            onMouseEnter={(e) => {
              if (hasText) e.currentTarget.style.boxShadow = `0 0 20px ${T.goldGlow}`;
            }}
            onMouseLeave={(e) => (e.currentTarget.style.boxShadow = 'none')}
          >
            {loading ? (
              <span className="flex items-center gap-2">
                Classifying
                <TypingDots />
              </span>
            ) : (
              'Delegate'
            )}
          </button>
        </div>
      </div>

      {/* Inline results */}
      {inlineResult && (
        <div className="relative z-[1] mb-10" style={{ animation: 'fadeUp 0.4s ease both' }}>
          {inlineResult.type === 'info' && (
            <div
              className="rounded-[14px]"
              style={{
                padding: '20px 24px',
                border: `1px solid ${T.greenDim}`,
                background: T.greenDim,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600, color: T.green, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8, fontFamily: T.mono }}>
                Answer
              </div>
              <div style={{ fontFamily: T.sans, fontSize: 14, color: T.cream60, lineHeight: 1.6 }}>
                {inlineResult.text}
              </div>
              <button
                type="button"
                onClick={() => {
                  setInlineResult(null);
                  inputRef.current?.focus();
                }}
                className="border-none bg-transparent cursor-pointer p-0 mt-3 text-xs font-medium"
                style={{ color: T.green, fontFamily: T.sans }}
              >
                Ask something else
              </button>
            </div>
          )}
          {inlineResult.type === 'clarification' && (
            <div
              className="rounded-[14px]"
              style={{
                padding: '20px 24px',
                border: `1px solid ${T.amberDim}`,
                background: T.amberDim,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600, color: T.amber, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8, fontFamily: T.mono }}>
                Clarification needed
              </div>
              <div style={{ fontFamily: T.sans, fontSize: 14, color: T.cream60, lineHeight: 1.6, marginBottom: 8 }}>
                {inlineResult.message}
              </div>
              <div style={{ fontFamily: T.mono, fontSize: 12, color: T.amber }}>
                Intent: {inlineResult.intent} · Confidence: {Math.round(inlineResult.confidence * 100)}%
              </div>
            </div>
          )}
          {inlineResult.type === 'usage_limit' && (
            <div
              className="rounded-[14px]"
              style={{
                padding: '20px 24px',
                border: `1px solid ${T.goldDim}`,
                background: T.goldGlow,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600, color: T.gold, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 8, fontFamily: T.mono }}>
                Limit reached
              </div>
              <div style={{ fontFamily: T.sans, fontSize: 14, color: T.cream60, lineHeight: 1.6, marginBottom: 12 }}>
                {inlineResult.message}
              </div>
              <button
                type="button"
                onClick={() => navigate('/settings')}
                className="border-none cursor-pointer font-semibold text-[13px]"
                style={{ padding: '8px 18px', borderRadius: 8, background: T.gold, color: T.bg }}
              >
                Upgrade your plan
              </button>
            </div>
          )}
        </div>
      )}

      {/* Two columns: Active Operations + Activity */}
      <div className="relative z-[1] home-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 260px', gap: 28 }}>
        <div>
          <div className="flex justify-between items-center mb-3.5">
            <h2
              style={{
                fontFamily: T.mono,
                fontSize: 10,
                fontWeight: 600,
                color: T.cream20,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
              }}
            >
              Active Operations
            </h2>
            <button
              type="button"
              onClick={() => navigate('/operations')}
              className="border-none bg-transparent cursor-pointer p-0 text-[11px] font-medium"
              style={{ color: T.gold, fontFamily: T.mono, letterSpacing: '0.05em', textTransform: 'uppercase' }}
            >
              View all
            </button>
          </div>
          <div className="flex flex-col gap-2">
            {activeOps.length === 0 ? (
              <GlassCard style={{ padding: 40, textAlign: 'center' }}>
                <div style={{ fontFamily: T.sans, fontSize: 14, fontWeight: 500, color: T.cream35, marginBottom: 4 }}>
                  No active tasks
                </div>
                <div style={{ fontFamily: T.sans, fontSize: 12, color: T.cream20 }}>
                  Describe a task above to get started.
                </div>
              </GlassCard>
            ) : (
              activeOps.map((n, i) => (
                <GlassCard
                  key={n.id}
                  hover
                  onClick={() => navigate(`/operations/${n.taskId}`)}
                  style={{
                    padding: '18px 20px',
                    animation: `fadeUp 0.5s ease ${0.3 + i * 0.08}s both`,
                  }}
                >
                  <div className="flex justify-between items-start mb-2.5">
                    <div className="flex-1 min-w-0">
                      <div
                        style={{
                          fontFamily: T.sans,
                          fontSize: 14,
                          fontWeight: 500,
                          color: T.cream,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {n.title}
                      </div>
                      <div style={{ fontFamily: T.mono, fontSize: 11, color: T.cream20, marginTop: 4 }}>
                        {n.message}
                      </div>
                    </div>
                    <div style={{ marginLeft: 12, flexShrink: 0 }}>
                      <StateBadge state={inferStateFromTitle(n.title)} />
                    </div>
                  </div>
                  <div
                    style={{
                      height: 2,
                      borderRadius: 1,
                      background: T.cream04,
                      overflow: 'hidden',
                    }}
                  >
                    <div
                      style={{
                        height: '100%',
                        borderRadius: 1,
                        background: n.title.includes('Needs You') || n.title.includes('Waiting') ? T.gold : T.green,
                        width: n.title.includes('Completed') ? '100%' : '66%',
                        transition: 'width 1.2s cubic-bezier(0.4,0,0.2,1)',
                      }}
                    />
                  </div>
                </GlassCard>
              ))
            )}
          </div>
        </div>

        <div style={{ animation: 'fadeUp 0.5s ease 0.5s both' }}>
          <h2
            style={{
              fontFamily: T.mono,
              fontSize: 10,
              fontWeight: 600,
              color: T.cream20,
              letterSpacing: '0.12em',
              textTransform: 'uppercase',
              marginBottom: 14,
            }}
          >
            Activity
          </h2>
          <div className="flex flex-col">
            {activityItems.length === 0 ? (
              <div style={{ fontFamily: T.sans, fontSize: 12, color: T.cream20, padding: '24px 0', textAlign: 'center' }}>
                No recent activity
              </div>
            ) : (
              activityItems.map((item, i) => {
                const kind =
                  item.type === 'task_update' && item.title.includes('Completed')
                    ? 'success'
                    : item.type === 'task_update' && (item.title.includes('Approval') || item.title.includes('Needs'))
                      ? 'action'
                      : 'info';
                const dotColor = kind === 'success' ? T.green : kind === 'action' ? T.gold : T.cream20;
                return (
                  <div
                    key={item.id}
                    style={{
                      padding: '11px 0',
                      borderBottom: i < activityItems.length - 1 ? `1px solid ${T.cream04}` : 'none',
                      opacity: showActivity ? 1 : 0,
                      transform: showActivity ? 'translateX(0)' : 'translateX(8px)',
                      transition: `all 0.4s ease ${i * 0.1}s`,
                    }}
                  >
                    <div className="flex items-start gap-2.5">
                      <div
                        style={{
                          width: 5,
                          height: 5,
                          borderRadius: '50%',
                          marginTop: 6,
                          flexShrink: 0,
                          background: dotColor,
                        }}
                      />
                      <div>
                        <div style={{ fontFamily: T.sans, fontSize: 12.5, color: T.cream60, lineHeight: 1.4 }}>
                          {item.title}
                        </div>
                        <div style={{ fontFamily: T.mono, fontSize: 10, color: T.cream12, marginTop: 3 }}>
                          {item.message.length > 60 ? `${item.message.slice(0, 60)}...` : item.message}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
